import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { inicioFimMesCorrenteLocalExtrato } from "../helpers/extrato-financeiro-datas";
import { mapExtratoFinanceiroApiToView } from "../helpers/map-extrato-financeiro-api-to-view";
import { listarExtratoFinanceiro, resincronizarExtratoFinanceiro } from "../services/extrato-financeiro.service";
import type { ResincronizarExtratoFinanceiroResponse } from "../services/extrato-financeiro.service";
import type {
  ExtratoFinanceiroApiResponse,
  ExtratoFinanceiroFiltros,
  ExtratoFinanceiroView,
} from "../types/extrato-financeiro.types";

function filtrosPadrao(contaFinanceiraIdInicial?: string): ExtratoFinanceiroFiltros {
  const { dataInicio, dataFim } = inicioFimMesCorrenteLocalExtrato();
  return {
    dataInicio,
    dataFim,
    contaFinanceiraId: contaFinanceiraIdInicial ?? "",
    tipo: "",
    categoria: "",
    origem: "",
    centroCustoId: "",
    pedidoId: "",
    tituloReceberId: "",
    tituloPagarId: "",
    pagamentoId: "",
    pagamentoInscricaoId: "",
    busca: "",
    page: 1,
    limit: 20,
    ocultarAjusteSistema: true,
  };
}

export type UseExtratoFinanceiroOptions = {
  contaFinanceiraIdInicial?: string;
};

export type UseExtratoFinanceiroResult = {
  filtros: ExtratoFinanceiroFiltros;
  setFiltros: Dispatch<SetStateAction<ExtratoFinanceiroFiltros>>;
  filtrosAplicados: ExtratoFinanceiroFiltros;
  data: ExtratoFinanceiroView | null;
  rawData: ExtratoFinanceiroApiResponse | null;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  page: number;
  limit: number;
  setPage: (p: number) => void;
  setLimit: (n: number) => void;
  setPagination: (page: number, limit: number) => void;
  aplicarFiltros: () => void;
  limparFiltros: () => void;
  refetch: () => void;
  resincronizarExtrato: () => Promise<ResincronizarExtratoFinanceiroResponse | null>;
  resincronizando: boolean;
  resyncFeedback: { tipo: "sucesso" | "erro"; mensagem: string } | null;
  limparResyncFeedback: () => void;
};

export function useExtratoFinanceiro(
  options: UseExtratoFinanceiroOptions = {},
): UseExtratoFinanceiroResult {
  const contaInicial = options.contaFinanceiraIdInicial?.trim() ?? "";
  const [filtros, setFiltros] = useState<ExtratoFinanceiroFiltros>(() =>
    filtrosPadrao(contaInicial || undefined),
  );
  const [filtrosAplicados, setFiltrosAplicados] =
    useState<ExtratoFinanceiroFiltros>(() => filtrosPadrao(contaInicial || undefined));

  const [rawData, setRawData] = useState<ExtratoFinanceiroApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [resincronizando, setResincronizando] = useState(false);
  const [resyncFeedback, setResyncFeedback] = useState<{
    tipo: "sucesso" | "erro";
    mensagem: string;
  } | null>(null);

  const page = filtrosAplicados.page ?? 1;
  const limit = filtrosAplicados.limit ?? 20;

  const setPage = useCallback((p: number) => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      page: Math.max(1, p),
    }));
  }, []);

  const setLimit = useCallback((n: number) => {
    const nextLimit = Math.min(100, Math.max(1, n));
    setFiltrosAplicados((prev) => ({
      ...prev,
      limit: nextLimit,
      ...(prev.limit !== nextLimit ? { page: 1 } : {}),
    }));
  }, []);

  const setPagination = useCallback((p: number, n: number) => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      page: Math.max(1, p),
      limit: Math.min(100, Math.max(1, n)),
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload: ExtratoFinanceiroFiltros = {
          ...filtrosAplicados,
          page: filtrosAplicados.page ?? 1,
          limit: filtrosAplicados.limit ?? 20,
        };
        const { data, status } = await listarExtratoFinanceiro(payload);
        if (cancelled) return;
        if (status === 403) {
          setError("Sem permissão para listar movimentações financeiras.");
          setRawData(null);
          return;
        }
        if (status === 404) {
          setError(
            "Endpoint não encontrado. Verifique GET /loja/financeiro/movimentacoes-financeiras.",
          );
          setRawData(null);
          return;
        }
        if (status !== 200 || !data) {
          setError("Não foi possível carregar o extrato financeiro.");
          setRawData(null);
          return;
        }
        setRawData(data);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : "Falha ao carregar extrato financeiro.";
        setError(msg);
        setRawData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [filtrosAplicados, tick]);

  const data = useMemo(() => mapExtratoFinanceiroApiToView(rawData), [rawData]);

  const isEmpty = useMemo(() => {
    if (error) return false;
    return !loading && data != null && data.itens.length === 0;
  }, [error, loading, data]);

  const aplicarFiltros = useCallback(() => {
    const next = {
      ...filtros,
      page: 1,
      limit: filtros.limit ?? 20,
      ocultarAjusteSistema:
        filtros.origem?.trim() === "AJUSTE_SISTEMA"
          ? false
          : filtros.ocultarAjusteSistema ?? true,
    };
    setFiltrosAplicados(next);
  }, [filtros]);

  const limparFiltros = useCallback(() => {
    const next = filtrosPadrao();
    setFiltros(next);
    setFiltrosAplicados(next);
  }, []);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  const limparResyncFeedback = useCallback(() => {
    setResyncFeedback(null);
  }, []);

  const resincronizarExtrato = useCallback(async () => {
    const pedidoId = filtros.pedidoId?.trim() || undefined;
    const pagamentoId = filtros.pagamentoId?.trim() || undefined;
    const pagamentoInscricaoRaw = filtros.pagamentoInscricaoId?.trim();
    const pagamentoInscricaoId = pagamentoInscricaoRaw
      ? Number.parseInt(pagamentoInscricaoRaw, 10)
      : undefined;

    if (!pedidoId && !pagamentoId && !pagamentoInscricaoId) {
      setResyncFeedback({
        tipo: "erro",
        mensagem:
          "Informe o ID do pedido, pagamento da loja ou pagamento de inscrição para resincronizar.",
      });
      return null;
    }

    if (
      pagamentoInscricaoRaw &&
      (!Number.isFinite(pagamentoInscricaoId) || (pagamentoInscricaoId ?? 0) <= 0)
    ) {
      setResyncFeedback({
        tipo: "erro",
        mensagem: "ID de pagamento de inscrição inválido.",
      });
      return null;
    }

    setResincronizando(true);
    setResyncFeedback(null);
    try {
      const { data, status, error } = await resincronizarExtratoFinanceiro({
        pedidoId,
        pagamentoId,
        pagamentoInscricaoId,
      });
      if (status !== 200 || !data) {
        setResyncFeedback({
          tipo: "erro",
          mensagem: error ?? "Não foi possível resincronizar o extrato.",
        });
        return null;
      }

      const partes = [
        `${data.movimentacoesCriadas} movimentação(ões) criada(s)`,
        data.estornosRegistrados > 0
          ? `${data.estornosRegistrados} estorno(s) registrado(s)`
          : null,
        `${data.itensProcessados} item(ns) processado(s)`,
      ].filter(Boolean);

      setResyncFeedback({
        tipo: "sucesso",
        mensagem: `Extrato resincronizado: ${partes.join(" · ")}.`,
      });
      setTick((n) => n + 1);
      return data;
    } finally {
      setResincronizando(false);
    }
  }, [filtros.pedidoId, filtros.pagamentoId, filtros.pagamentoInscricaoId]);

  return {
    filtros,
    setFiltros,
    filtrosAplicados,
    data,
    rawData,
    loading,
    error,
    isEmpty,
    page,
    limit,
    setPage,
    setLimit,
    setPagination,
    aplicarFiltros,
    limparFiltros,
    refetch,
    resincronizarExtrato,
    resincronizando,
    resyncFeedback,
    limparResyncFeedback,
  };
}
